// src/components/PaymentForm.tsx

import React, { useState, useRef } from 'react';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { fetchAuthSession } from 'aws-amplify/auth'; // For Cognito ID Token
import { getAmplifyCustomOutput } from '../lib/environment';

// Define the structure for billing contact information
// This should align with Square's 'Contact' type used for billing in SCA.
interface BillingContactDetails {
  givenName: string;
  familyName: string;
  postalCode: string;
  countryCode: string; // keep for SCA — default 'US', not shown to user
}

// Define the props expected by your custom payment form component
interface MyPaymentFormProps {
  amount: number; // Payment amount in dollars (e.g., 19.00 for $19.00)
  onSuccess: (paymentDetails: { paymentId?: string; receiptUrl?: string; orderId?: string; paymentStatus?: string }) => void;
  onFailure: (errorMessage: string) => void; // Unified failure callback
}

const MyPaymentForm: React.FC<MyPaymentFormProps> = ({ amount, onSuccess, onFailure }) => {
  const applicationId = import.meta.env.VITE_SQUARE_APPLICATION_ID;
  const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;

  const payButtonRef = useRef<HTMLDivElement>(null);
  const cardFieldRef = useRef<HTMLDivElement>(null);

  const handleBillingFocus = () => {
    setTimeout(() => {
      const el = payButtonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const headerOffset = 80;
      const scrollTop = window.scrollY + rect.top - headerOffset;
      window.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }, 300);
  };

  const handleZipFocus = () => {
    setTimeout(() => {
      cardFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  // State to hold the billing contact details entered by the user
  const [billingContact, setBillingContact] = useState<BillingContactDetails>({
    givenName: '',
    familyName: '',
    postalCode: '',
    countryCode: 'US',
  });

  // Handler to update billing contact state from form inputs
  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBillingContact(prev => ({ ...prev, [name]: value }));
  };

  // Configuration check
  if (!applicationId || !locationId) {
    void ["Square Application ID or Location ID is missing from .env variables."];
    // It's good practice to inform the user if the payment system is not configured
    return <p style={{ color: 'red' }}>Payment system is currently unavailable. Please contact support.</p>;
  }

  // Handler for when Square successfully tokenizes the card details
  const handleCardTokenizeResponseReceived = async (tokenResult: any, verifiedBuyer: any) => {
    void ['Sandbox Nonce:', tokenResult.token];
    void ['Square Tokenization Result:', tokenResult, 'Verified Buyer (SCA details):', verifiedBuyer];

    if (!tokenResult.token) {
      const errorMsg = "Failed to tokenize card details with Square. Please try again.";
      void [errorMsg];
      onFailure(errorMsg);
      return;
    }

    try {
      // 1. Fetch the current user's session to get the ID token for authorization
      let idToken;
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        idToken = session.tokens?.idToken?.toString();
      } catch (authError: any) {
        void ['Authentication error fetching session:', authError];
        onFailure('Authentication error. Please ensure you are signed in and try again.');
        return; // Stop if no auth token can be retrieved
      }

      if (!idToken) {
        void ["CRITICAL: idToken is UNDEFINED or NULL before fetch!"];
        onFailure('Unable to retrieve authentication token. Please sign in again.');
        return;
      }

      // 2. Send the payment token (nonce) and other details to your backend
      // IMPORTANT: Replace with your actual API endpoint.
      // Using '/api/process-payment' assumes you have a Vite proxy set up.
      // If not, use the full direct API Gateway URL for your dev environment.
        const apiBase = getAmplifyCustomOutput('PaymentAPIEndpoint');
        if (!apiBase) {
          throw new Error('PaymentAPIEndpoint not configured');
        }
        const apiUrl = `${apiBase}process-payment`;
      // const apiUrl = 'https://<your-api-id>.execute-api.<your-region>.amazonaws.com/<your-stage>/process-payment'; // For direct call
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`, // Add the Cognito ID token
        },
        body: JSON.stringify({
          token: tokenResult.token, // This is the Square payment nonce (sourceId)
          amount: amount,          // Amount in dollars
          // Optionally send billingContact if your backend needs it for records or further validation
          // billingContact: billingContact,
        }),
      });

      // 3. Handle the backend response
      if (!response.ok) {
        // Attempt to parse error from backend if response is not ok (e.g., 4xx, 5xx errors)
        let errorData = { error: `Payment processing failed with status: ${response.status}` };
        try {
          const errorJson = await response.json();
          errorData = { error: errorJson.error || errorJson.message || errorData.error };
        } catch (parseError) {
          void ['Could not parse error response from backend as JSON:', response.statusText];
          // If parsing fails, use the status text or a generic message
          errorData = { error: `Payment processing failed: ${response.statusText || response.status}` };
        }
        throw new Error(errorData.error);
      }

      const data = await response.json(); // Parse successful JSON response from backend

      if (data.status === 'SUCCESS') {
        void ['Payment successful via backend:', data];
        onSuccess({
          paymentId: data.paymentId,
          receiptUrl: data.receiptUrl,
          orderId: data.orderId,
          paymentStatus: data.paymentStatus
        });
      } else {
        // Handle cases where backend responds 200 OK but with a business logic failure status
        throw new Error(data.error || 'Payment was not successful. Please check details or try again.');
      }
    } catch (error: any) {
      void ['Error during payment submission or processing:', error];
      onFailure(error.message || "An unexpected error occurred while processing your payment.");
    }
  };

  // Brand presentation tokens (UI only).
  const fieldsetStyle: React.CSSProperties = {
    background: "#FFFFFF",
    border: "2.5px solid #1A1A1A",
    borderRadius: 16,
    boxShadow: "4px 4px 0px #1A1A1A",
    padding: 20,
    marginBottom: 20,
    boxSizing: "border-box",
  };
  const legendStyle: React.CSSProperties = {
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    color: "#1A1A1A",
    padding: "0 8px",
  };
  const inputStyle: React.CSSProperties = {
    border: "2px solid #E05A3A",
    borderRadius: 12,
    padding: "10px 14px",
    fontFamily: "'Nunito', sans-serif",
    fontSize: 15,
    width: "100%",
    background: "#FDF6EE",
    marginBottom: 12,
    color: "#1A1A1A",
    outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: "#1A1A1A",
    marginBottom: 4,
  };

  return (
    <div className="payment-form-container">
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Billing Information</legend>

        <div>
          <label htmlFor="givenName" style={labelStyle}>First Name:</label>
          <input
            id="givenName"
            name="givenName"
            type="text"
            value={billingContact.givenName}
            onChange={handleBillingChange}
            onFocus={handleBillingFocus}
            required
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="familyName" style={labelStyle}>Last Name:</label>
          <input
            id="familyName"
            name="familyName"
            type="text"
            value={billingContact.familyName}
            onChange={handleBillingChange}
            onFocus={handleBillingFocus}
            required
            style={inputStyle}
          />
        </div>

        <div>
          <label htmlFor="postalCode" style={labelStyle}>ZIP/Postal Code (Billing):</label>
          <input
            id="postalCode"
            name="postalCode"
            type="text"
            value={billingContact.postalCode}
            onChange={handleBillingChange}
            onFocus={handleZipFocus}
            required
            style={inputStyle}
          />
        </div>
      </fieldset>


      <PaymentForm
        applicationId={applicationId} // Non-null assertion used as we check above
        locationId={locationId}     // Non-null assertion used
        cardTokenizeResponseReceived={handleCardTokenizeResponseReceived}
        createVerificationDetails={() => ({ // This function is crucial for SCA
          amount: amount.toFixed(2),
          currencyCode: 'USD',
          intent: 'CHARGE',
          billingContact: {
            givenName: billingContact.givenName,
            familyName: billingContact.familyName,
            postalCode: billingContact.postalCode,
            countryCode: billingContact.countryCode,
          },
        })}
      >
        {/* The CreditCard component from Square SDK handles card number, expiry, CVV, and card postal code */}

<div ref={payButtonRef}>
<div ref={cardFieldRef}>
<CreditCard
  buttonProps={{
    children: 'Pay Now',
    css: {
      backgroundColor: '#E05A3A',
      borderColor: '#1A1A1A',
      borderWidth: '3px',
      borderStyle: 'solid',
      borderRadius: '100px',
      color: 'white',
      padding: '14px 24px',
      fontFamily: "'Fredoka', sans-serif",
      fontSize: '18px',
      fontWeight: 700,
      width: '100%',
      boxShadow: '4px 4px 0px #1A1A1A',
      '&:hover': {
        backgroundColor: '#C44B2E',
      },
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
  }}
/>
</div>
</div>

      </PaymentForm>
    </div>
  );
};

export default MyPaymentForm;
